import React from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { highlightRanges, parseInline, parseMarkdown } from '../lib/consignesSearch.mjs';

// Texte avec les mots recherchés surlignés.
export const Highlight = ({ text, query }) => {
  const value = String(text || '').normalize('NFC');
  const ranges = query ? highlightRanges(value, query) : [];
  if (!ranges.length) return value;
  const parts = [];
  let last = 0;
  ranges.forEach(([start, end], index) => {
    if (start > last) parts.push(value.slice(last, start));
    parts.push(
      <mark key={index} className="rounded bg-amber-300 px-0.5 text-zinc-950">
        {value.slice(start, end)}
      </mark>
    );
    last = end;
  });
  if (last < value.length) parts.push(value.slice(last));
  return parts;
};

const Inline = ({ text, query }) => parseInline(text).map((part, index) => {
  if (part.t === 'b') return <strong key={index} className="font-bold text-foreground"><Highlight text={part.v} query={query} /></strong>;
  if (part.t === 'i') return <em key={index} className="text-muted-foreground"><Highlight text={part.v} query={query} /></em>;
  if (part.t === 'link') {
    return (
      <a
        key={index}
        href={part.href}
        className="inline-flex min-h-11 items-center font-semibold text-primary underline underline-offset-4 break-all"
      >
        <Highlight text={part.v} query={query} />
      </a>
    );
  }
  return <React.Fragment key={index}><Highlight text={part.v} query={query} /></React.Fragment>;
});

const Blocks = ({ blocks, prefix, ctx }) => blocks.map((block, index) => {
  const key = `${prefix}${index}`;
  const { query, focusKey } = ctx;
  const focus = (k) => (focusKey === k ? 'ring-2 ring-amber-400 rounded-md' : '');
  if (block.type === 'h2') {
    return (
      <h2 key={key} data-ckey={key} className={`mt-8 mb-3 text-lg md:text-xl font-black uppercase tracking-wide text-primary first:mt-0 ${focus(key)}`}>
        <Highlight text={block.text} query={query} />
      </h2>
    );
  }
  if (block.type === 'p') {
    return (
      <p key={key} data-ckey={key} className={`my-3 text-[17px] leading-relaxed text-foreground/90 ${focus(key)}`}>
        <Inline text={block.text} query={query} />
      </p>
    );
  }
  if (block.type === 'callout') {
    return (
      <div key={key} data-ckey={key} className={`my-4 flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-[17px] leading-relaxed ${focus(key)}`}>
        <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-amber-400" aria-hidden="true" />
        <div><Inline text={block.text} query={query} /></div>
      </div>
    );
  }
  if (block.type === 'list') {
    return (
      <ul key={key} className="my-3 space-y-2" role={block.ordered ? 'list' : undefined} aria-label={block.ordered ? 'Étapes' : undefined}>
        {block.items.map((item, itemIndex) => {
          const itemKey = `${key}.${itemIndex}`;
          return (
            <li key={itemKey} data-ckey={itemKey} className={`flex gap-3 text-[17px] leading-relaxed text-foreground/90 ${block.ordered ? 'rounded-lg border border-border bg-card/60 p-3' : ''} ${focus(itemKey)}`}>
              {block.ordered ? (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-black text-primary-foreground" aria-hidden="true">
                  {item.number}
                </span>
              ) : (
                <span className="mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              )}
              <div className="min-w-0">
                <Inline text={item.text} query={query} />
                {item.children.length > 0 && (
                  <ul className="mt-2 space-y-1.5 border-l border-border pl-4">
                    {item.children.map((child, childIndex) => {
                      const childKey = `${itemKey}.${childIndex}`;
                      return (
                        <li key={childKey} data-ckey={childKey} className={`text-[16px] leading-relaxed text-foreground/85 ${focus(childKey)}`}>
                          <Inline text={child} query={query} />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }
  if (block.type === 'article') {
    const open = ctx.isOpen(block.title);
    const panelId = `article-${ctx.sectionId}-${key.replace(/\./g, '-')}`;
    return (
      <div key={key} data-ckey={key} className={`my-2 rounded-lg border border-border bg-card ${focus(key)}`}>
        <button
          type="button"
          onClick={() => ctx.onToggle(block.title)}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid="consigne-article"
          className="flex min-h-14 w-full items-center justify-between gap-3 px-4 py-3 text-left text-[16px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span><Highlight text={block.title} query={query} /></span>
          <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
        {open && (
          <div id={panelId} className="border-t border-border px-4 pb-3">
            <Blocks blocks={block.blocks} prefix={`${key}.`} ctx={ctx} />
          </div>
        )}
      </div>
    );
  }
  return null;
});

export function articleTitles(body) {
  return parseMarkdown(body).filter((block) => block.type === 'article').map((block) => block.title);
}

const ConsignesMarkdown = ({ body, sectionId = 'apercu', query = '', focusKey = '', isOpen = () => true, onToggle = () => {} }) => {
  const blocks = parseMarkdown(body);
  return (
    <div className="consignes-markdown">
      <Blocks blocks={blocks} prefix="" ctx={{ query, focusKey, isOpen, onToggle, sectionId }} />
    </div>
  );
};

export default ConsignesMarkdown;
